import React from "react";

interface BookingConfirmationTemplateProps {
  userName: string;
  spaceName: string;
  bookingDate: string;
  bookingTime: string;
  bookingId: string;
  totalPrice: string;
  spaceImage?: string;
  spaceAddress: string;
}

const BookingConfirmationTemplate: React.FC<BookingConfirmationTemplateProps> = ({
  userName,
  spaceName,
  bookingDate,
  bookingTime,
  bookingId,
  totalPrice,
  spaceImage = "https://via.placeholder.com/400x200/667eea/ffffff?text=Espace+EventToBee",
  spaceAddress,
}) => {
  const templateHTML = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Confirmation de réservation - EventToBee 🐝</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #333;
        }
        .container {
            max-width: 650px;
            margin: 20px auto;
            background: white;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 20px 60px rgba(0,0,0,0.1);
        }
        .header {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            padding: 30px;
            text-align: center;
            color: white;
            position: relative;
        }
        .header::before {
            content: '✅';
            position: absolute;
            top: 10px;
            right: 20px;
            font-size: 24px;
            opacity: 0.7;
        }
        .logo {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 10px;
        }
        .confirmation-title {
            font-size: 24px;
            margin-bottom: 5px;
        }
        .confirmation-subtitle {
            font-size: 16px;
            opacity: 0.9;
        }
        .content {
            padding: 30px;
        }
        .booking-card {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border-radius: 15px;
            padding: 25px;
            margin: 20px 0;
            border-left: 5px solid #10b981;
        }
        .space-image {
            width: 100%;
            height: 200px;
            object-fit: cover;
            border-radius: 10px;
            margin-bottom: 20px;
        }
        .space-name {
            font-size: 22px;
            font-weight: bold;
            color: #1e293b;
            margin-bottom: 10px;
        }
        .booking-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 20px 0;
        }
        .detail-item {
            background: white;
            padding: 15px;
            border-radius: 10px;
            border: 2px solid #e2e8f0;
        }
        .detail-label {
            font-size: 12px;
            text-transform: uppercase;
            color: #64748b;
            font-weight: bold;
            margin-bottom: 5px;
        }
        .detail-value {
            font-size: 16px;
            font-weight: bold;
            color: #1e293b;
        }
        .price-highlight {
            background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
            color: #92400e;
            padding: 20px;
            border-radius: 15px;
            text-align: center;
            margin: 20px 0;
            border: 2px solid #f59e0b;
        }
        .price-label {
            font-size: 14px;
            margin-bottom: 5px;
        }
        .price-amount {
            font-size: 28px;
            font-weight: bold;
        }
        .action-buttons {
            display: flex;
            gap: 15px;
            margin: 30px 0;
            justify-content: center;
        }
        .btn {
            padding: 12px 25px;
            border-radius: 50px;
            text-decoration: none;
            font-weight: bold;
            text-align: center;
            transition: transform 0.3s ease;
        }
        .btn:hover {
            transform: translateY(-2px);
        }
        .btn-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }
        .btn-secondary {
            background: white;
            color: #667eea;
            border: 2px solid #667eea;
        }
        .info-section {
            background: #f1f5f9;
            padding: 20px;
            border-radius: 15px;
            margin: 20px 0;
        }
        .info-title {
            font-weight: bold;
            margin-bottom: 10px;
            color: #334155;
        }
        .info-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .info-list li {
            padding: 5px 0;
            border-bottom: 1px solid #e2e8f0;
        }
        .info-list li:last-child {
            border-bottom: none;
        }
        .footer {
            background: #f8f9fa;
            padding: 25px;
            text-align: center;
            font-size: 14px;
            color: #666;
            border-top: 1px solid #eee;
        }
        .emergency-contact {
            background: #fee2e2;
            border: 2px solid #fca5a5;
            color: #991b1b;
            padding: 15px;
            border-radius: 10px;
            margin: 20px 0;
            text-align: center;
        }
        @media (max-width: 600px) {
            .container { margin: 10px; }
            .booking-details { grid-template-columns: 1fr; }
            .action-buttons { flex-direction: column; }
            .btn { margin: 5px 0; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">🐝 EventToBee</div>
            <div class="confirmation-title">Réservation Confirmée !</div>
            <div class="confirmation-subtitle">Votre espace vous attend</div>
        </div>

        <div class="content">
            <p style="font-size: 16px; margin-bottom: 20px;">
                Bonjour <strong>${userName}</strong>,
            </p>

            <p style="color: #10b981; font-weight: bold; font-size: 16px;">
                🎉 Excellente nouvelle ! Votre réservation est confirmée.
            </p>

            <div class="booking-card">
                <img src="${spaceImage}" alt="${spaceName}" class="space-image">

                <div class="space-name">📍 ${spaceName}</div>
                <p style="color: #64748b; margin: 0;">${spaceAddress}</p>

                <div class="booking-details">
                    <div class="detail-item">
                        <div class="detail-label">📅 Date</div>
                        <div class="detail-value">${bookingDate}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">⏰ Horaire</div>
                        <div class="detail-value">${bookingTime}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">🔖 N° Réservation</div>
                        <div class="detail-value">#${bookingId}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">👤 Réservé par</div>
                        <div class="detail-value">${userName}</div>
                    </div>
                </div>

                <div class="price-highlight">
                    <div class="price-label">💰 Total payé</div>
                    <div class="price-amount">${totalPrice}€</div>
                </div>
            </div>

            <div class="action-buttons">
                <a href="#" class="btn btn-primary">📱 Voir ma réservation</a>
                <a href="#" class="btn btn-secondary">📄 Télécharger le PDF</a>
            </div>

            <div class="info-section">
                <div class="info-title">📋 Informations importantes</div>
                <ul class="info-list">
                    <li>🔑 <strong>Accès :</strong> Les clés/codes vous seront communiqués 24h avant</li>
                    <li>⏰ <strong>Ponctualité :</strong> Merci d'arriver à l'heure convenue</li>
                    <li>🧹 <strong>État des lieux :</strong> Laissez l'espace dans l'état où vous l'avez trouvé</li>
                    <li>📞 <strong>Contact :</strong> Le propriétaire vous contactera prochainement</li>
                </ul>
            </div>

            <div class="emergency-contact">
                <strong>🚨 Urgence le jour J ?</strong><br>
                Contactez-nous au <strong>01 23 45 67 89</strong><br>
                ou par email : <strong>urgence@eventtobee.com</strong>
            </div>

            <p style="text-align: center; color: #64748b; margin-top: 30px;">
                <strong>Merci de faire confiance à EventToBee ! 🙏</strong><br>
                Nous espérons que votre événement sera mémorable.
            </p>
        </div>

        <div class="footer">
            <p>
                <strong>EventToBee</strong> - La ruche des espaces événementiels 🐝<br>
                <a href="tel:0123456789" style="color: #667eea;">01 23 45 67 89</a> |
                <a href="mailto:contact@eventtobee.com" style="color: #667eea;">contact@eventtobee.com</a>
            </p>

            <p style="font-size: 12px; color: #999; margin-top: 15px;">
                Besoin d'aide ? <a href="#" style="color: #667eea;">Centre d'aide</a> |
                <a href="#" style="color: #667eea;">Modifier ma réservation</a> |
                <a href="#" style="color: #667eea;">Annuler</a>
            </p>
        </div>
    </div>
</body>
</html>`;

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">📧 Template: Confirmation de Réservation</h3>
      <div className="mb-4">
        <button
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          onClick={() => navigator.clipboard.writeText(templateHTML)}
        >
          📋 Copier le code HTML
        </button>
      </div>
      <div
        className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto"
        dangerouslySetInnerHTML={{ __html: templateHTML }}
      />
    </div>
  );
};

export default BookingConfirmationTemplate;
